// Area catalog — areas are first-class entities in TRUE V2 (never a page-local array).
// The kinds below cover the ordinary field walk of a residential inspection. Display names
// re-express the product's established Chinese wording (knowledge, see
// docs/KNOWLEDGE-IMPORT-MANIFEST.md K-14); the catalog structure itself is new.
//
// A kind is the CHECKLIST TEMPLATE SELECTOR of an area, nothing more: every area instance
// (第二个卫生间, 独立车库, 爬行空间, 二楼卧室 3) is its own V2Area record with its own name,
// floor and checklist rows. Which instances a house gets is decided by the house plan
// (src/domain/housePlan.ts) at creation time and by the inspector afterwards.
export type AreaKind =
  | 'exterior'
  | 'roof'
  | 'garage'
  | 'basement'
  | 'structure'
  | 'electrical'
  | 'plumbing'
  | 'waterHeater'
  | 'hvac'
  | 'attic'
  | 'interior'
  | 'kitchen'
  | 'bathroom'
  | 'bedroom'
  | 'safety'
  | 'other';

export type AreaGroup = 'exterior' | 'spaces' | 'systems' | 'rooms' | 'wholeHouse';

export interface AreaKindDefinition {
  kind: AreaKind;
  label: string;
  group: AreaGroup;
  // More than one instance is the normal case (each bedroom, each bathroom, second kitchen,
  // attached + detached garage). Single-instance kinds are whole-house systems.
  multiInstance: boolean;
}

// Field-walk order: outside → spaces → systems → rooms → whole-house safety review.
export const AREA_CATALOG: readonly AreaKindDefinition[] = Object.freeze([
  { kind: 'exterior', label: '外部 / 场地', group: 'exterior', multiInstance: false },
  { kind: 'roof', label: '屋顶', group: 'exterior', multiInstance: false },
  { kind: 'garage', label: '车库', group: 'spaces', multiInstance: true },
  { kind: 'basement', label: '地下室 / 爬行空间', group: 'spaces', multiInstance: true },
  { kind: 'structure', label: '结构', group: 'systems', multiInstance: false },
  { kind: 'electrical', label: '电气系统', group: 'systems', multiInstance: false },
  { kind: 'plumbing', label: '水管系统', group: 'systems', multiInstance: false },
  { kind: 'waterHeater', label: '热水器', group: 'systems', multiInstance: false },
  { kind: 'hvac', label: '供暖 / 空调', group: 'systems', multiInstance: false },
  { kind: 'attic', label: '阁楼', group: 'spaces', multiInstance: false },
  { kind: 'interior', label: '室内 / 起居空间', group: 'rooms', multiInstance: true },
  { kind: 'kitchen', label: '厨房', group: 'rooms', multiInstance: true },
  { kind: 'bathroom', label: '卫生间', group: 'rooms', multiInstance: true },
  { kind: 'bedroom', label: '卧室', group: 'rooms', multiInstance: true },
  { kind: 'safety', label: '安全复核', group: 'wholeHouse', multiInstance: false },
  { kind: 'other', label: '其他区域', group: 'spaces', multiInstance: true }
]);

export const AREA_KIND_LABELS: Record<AreaKind, string> = AREA_CATALOG.reduce(
  (acc, def) => {
    acc[def.kind] = def.label;
    return acc;
  },
  {} as Record<AreaKind, string>
);

export const AREA_KIND_ORDER: readonly AreaKind[] = Object.freeze(AREA_CATALOG.map((d) => d.kind));

export function areaDefinition(kind: AreaKind): AreaKindDefinition {
  const def = AREA_CATALOG.find((d) => d.kind === kind);
  if (!def) throw new Error(`unknown area kind: ${kind}`);
  return def;
}

// Next free instance name for a stem: "卫生间" → "卫生间 2" → "卫生间 3"; with numbered
// instances already present ("二楼卧室 1..3") the next one continues the series ("二楼卧室 4").
export function nextInstanceName(base: string, existingNames: readonly string[]): string {
  const stem = base.trim();
  let highest = 0;
  for (const raw of existingNames) {
    const name = raw.trim();
    if (name === stem) {
      highest = Math.max(highest, 1);
    } else if (name.startsWith(`${stem} `)) {
      const suffix = name.slice(stem.length + 1).trim();
      const n = Number.parseInt(suffix, 10);
      if (Number.isFinite(n) && String(n) === suffix) highest = Math.max(highest, n);
    }
  }
  return highest === 0 ? stem : `${stem} ${highest + 1}`;
}

// Kind-label based variant kept for callers that add an instance without a preset name.
export function nextAreaName(kind: AreaKind, existingNames: readonly string[]): string {
  return nextInstanceName(AREA_KIND_LABELS[kind], existingNames);
}
